import React, { useState, useEffect, useRef, useCallback } from 'react'
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom'
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader.js"
import Alert from '../common/Alert'
import tripContractService from '../../services/tripContractService'
import BatchPaymentModal from './BatchPaymentModal'
import { Plus } from 'lucide-react'
import ListFilterPanel from '../common/ListFilterPanel.js'
import Button from '../common/Button.js'
import RowActionsMenu from '../common/RowActionsMenu.js'
import { useTranslation } from 'react-i18next';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js'
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js'
import ListPagination from '../common/ListPagination.js'
import SearchSubmitInput from '../common/SearchSubmitInput.js'


const TripContractList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate()
  const [trips, setTrips] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [filters, setFilters] = useState({
    search: '',
    contract_party: '',
    vehicle: '',
    status: '',
  })
  const perPage = 10
  const [deletingId, setDeletingId] = useState(null)
  const [activeDropdown, setActiveDropdown] = useState(null)
  const dropdownRef = useRef(null)
  const [batchPaymentModal, setBatchPaymentModal] = useState({
    isOpen: false,
    selectedPartyId: null,
    selectedPartyName: null
  })

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const fetchTrips = useCallback(async () => {
    try {
      setLoading(true)
      const response = await tripContractService.getAll({
        ...filters,
        page: currentPage,
        per_page: perPage,
      })
      setTrips((prev) => mergeLoadedPage(currentPage, response.data.data || [], prev))
      setTotalPages(response.data.pagination?.pages || 1)
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load trip contracts')
      console.error(err)
      if (currentPage <= 1) setTrips([])
    } finally {
      setLoading(false)
    }
  }, [filters, currentPage, perPage])

  usePaginatedListFetch({
    fetchPage: fetchTrips,
    setCurrentPage,
    currentPage,
    resetPageDeps: [filters.search, filters.contract_party, filters.vehicle, filters.status],
    debounceWhen: filters.search,
    debounceMs: 0,
  })

  const handleDelete = async (tripId) => {
    if (!window.confirm('Are you sure you want to delete this trip contract?')) {
      return
    }

    try {
      setDeletingId(tripId)
      await tripContractService.delete(tripId)
      Alert.success('Trip contract deleted successfully!')
      fetchTrips()
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to delete trip contract')
    } finally {
      setDeletingId(null)
    }
  }

  const handlePartyChange = (e) => {
    setFilters({ ...filters, contract_party: e.target.value })
  }

  const handleVehicleChange = (e) => {
    setFilters({ ...filters, vehicle: e.target.value })
  }

  const handleStatusChange = (e) => {
    setFilters({ ...filters, status: e.target.value })
  }

  const clearFilters = () => {
    setFilters({ search: '', contract_party: '', vehicle: '', status: '' })
    setCurrentPage(1)
  }

  const hasActiveFilters = filters.search || filters.contract_party || filters.vehicle || filters.status

  const openBatchPaymentModal = (partyId, partyName) => {
    setBatchPaymentModal({
      isOpen: true,
      selectedPartyId: partyId,
      selectedPartyName: partyName
    })
  }

  const closeBatchPaymentModal = () => {
    setBatchPaymentModal({
      isOpen: false,
      selectedPartyId: null,
      selectedPartyName: null
    })
    fetchTrips()
  }

  const capitalizeStatus = (status) => {
    return status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : ''
  }

  const formatCurrency = (value) => {
    return parseFloat(value || 0).toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getStatusBadgeColor = (status) => {
    const colors = {
      'paid': 'bg-green-100 text-green-700',
      'pending': 'bg-yellow-100 text-yellow-700',
      'active': 'bg-blue-100 text-blue-700',
      'cancelled': 'bg-red-100 text-red-700'
    }
    return colors[status] || 'bg-gray-100 text-gray-700'
  }

  return (
    <DashboardLayout>
      <div className="max-w-full">
        <ListPageHeader
          title={t('pages.tripContract.title')}
          subtitle="Manage trip contracts, payments, and invoices"
          titleClassName="text-secondary"
          className="mb-6"
        >
          <Button mobileIconOnly variant="primary" onClick={() => navigate('/trip-contracts/add')} icon={Plus}>
            Add Trip Contract
          </Button>
        </ListPageHeader>

        <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}>
          <div>
            <SearchSubmitInput
              value={filters.search}
              onSearch={(value) => setFilters((prev) => ({ ...prev, search: value }))}
              placeholder="Search trip ID, invoice..."
              inputClassName="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          <div>
            <select
              value={filters.contract_party}
              onChange={handlePartyChange}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="">All Parties</option>
              <option value="party1">Party 1</option>
              <option value="party2">Party 2</option>
            </select>
          </div>

          <div>
            <select
              value={filters.vehicle}
              onChange={handleVehicleChange}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="">All Vehicles</option>
              <option value="vehicle1">Vehicle 1</option>
              <option value="vehicle2">Vehicle 2</option>
            </select>
          </div>

          <div>
            <select
              value={filters.status}
              onChange={handleStatusChange}
              className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </ListFilterPanel>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading && currentPage === 1 ? (
            <div className="flex items-center justify-center py-20">
              <Loader fullScreen={false} size="default" message="Loading trip contracts..." />
            </div>
          ) : trips.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500 mb-4">No trip contracts found</p>
              <button
                onClick={() => navigate('/trip-contracts/add')}
                className="text-orange-500 hover:text-orange-600 font-medium"
              >
                Create the first trip contract
              </button>
            </div>
          ) : (
            <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Trip ID</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Invoice / Party</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Vehicle</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Location</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700">Total</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700">Paid</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700">Balance</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Status</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {trips.map((trip) => {
                    const balance = (trip.total_cost || 0) - (trip.paid_amount || 0)
                    return (
                      <tr key={trip.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <span className="font-semibold text-gray-900">{trip.trip_id}</span>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="font-medium text-gray-900">{trip.invoice_no}</div>
                          <div className="text-gray-600">{trip.contract_party_name}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {trip.vehicle_number}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {trip.delivery_location}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-semibold">
                          {formatCurrency(trip.total_cost)}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-semibold text-green-600">
                          {formatCurrency(trip.paid_amount)}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-semibold text-orange-600">
                          {formatCurrency(balance)}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeColor(trip.status)}`}>
                            {capitalizeStatus(trip.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <RowActionsMenu
                            isOpen={activeDropdown === trip.id}
                            onToggle={() => setActiveDropdown(activeDropdown === trip.id ? null : trip.id)}
                            onClose={() => setActiveDropdown(null)}
                            menuRef={dropdownRef}
                            onView={() => navigate(`/trip-contracts/view/${trip.id}`)}
                            onEdit={() => navigate(`/trip-contracts/edit/${trip.id}`)}
                            onDelete={() => handleDelete(trip.id)}
                            extraItems={[
                              {
                                label: 'Batch Payment',
                                icon: '⚡',
                                variant: 'primary',
                                onClick: () => openBatchPaymentModal(trip.contract_party_id, trip.contract_party_name),
                              },
                            ]}
                            className="relative inline-block"
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <ListPagination
              currentPage={currentPage}
              totalPages={totalPages}
              loading={loading}
              onNext={() => setCurrentPage((page) => page + 1)}
            />
            </>
          )}
        </div>
      </div>

      {/* Batch Payment Modal */}
      {batchPaymentModal.isOpen && (
        <BatchPaymentModal
          isOpen={batchPaymentModal.isOpen}
          partyId={batchPaymentModal.selectedPartyId}
          partyName={batchPaymentModal.selectedPartyName}
          onClose={closeBatchPaymentModal}
        />
      )}
    </DashboardLayout>
  )
}

export default TripContractList
